import React, { useState, useEffect, useMemo, useRef } from 'react'
import { Table, Select, InputNumber, Button, Modal, Tag, Spin, Input, Progress, Tooltip as AntTooltip, Drawer, Popover, Typography, message, Checkbox, Pagination } from 'antd'
import {
  SearchOutlined, TeamOutlined, BarChartOutlined, TrophyOutlined,
  WomanOutlined, CheckCircleOutlined, UserOutlined, StarOutlined,
  CopyOutlined, LeftOutlined, RightOutlined, FilterOutlined,
} from '@ant-design/icons'
import {
  BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  ComposedChart, Line,
} from 'recharts'
import styled from 'styled-components'

const { Option } = Select

// ── Summary KPI cards ─────────────────────────────────────────

const SummaryGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 0 0 12px;
  margin-bottom: 8px;
`

const SummaryKPICard = styled.div`
  background: #ffffff;
  border: 1px solid ${p => p.$active ? p.$color || '#3249D7' : '#e8ecf4'};
  border-radius: 10px;
  padding: 10px 14px;
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 0 0 auto;
  white-space: nowrap;
  box-shadow: 0 1px 4px rgba(0,0,0,0.05);
  transition: transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s;
  cursor: ${p => p.$clickable ? 'pointer' : 'default'};
  &:hover {
    transform: ${p => p.$clickable ? 'translateY(-2px)' : 'none'};
    box-shadow: 0 4px 16px rgba(50,73,215,0.10);
    border-color: ${p => p.$color || '#3249D7'};
  }
`

const SKPIIcon = styled.div`
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: ${p => (p.color || '#3249D7')}15;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 17px;
  color: ${p => p.color || '#3249D7'};
  flex-shrink: 0;
`

const SKPIValue = styled.div`
  font-size: 19px;
  font-weight: 800;
  color: #1a1a2e;
  line-height: 1.1;
  white-space: nowrap;
`

const SKPILabel = styled.div`
  font-size: 11px;
  color: #7b8499;
  margin-top: 2px;
  font-weight: 500;
`

// ── Filter styled components ──────────────────────────────────

const FilterRow = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 20px;
  align-items: flex-end;
`

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  label {
    font-size: 12px;
    color: #666;
    font-weight: 500;
  }
`

// ── Profile modal styled components ──────────────────────────

const ProfileWrap = styled.div`
  font-family: inherit;
`

const ProfileHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 16px;
  background: #fff;
  border: 1px solid #eef0fb;
  border-radius: 12px;
  margin-bottom: 16px;
`

const Avatar = styled.div`
  width: 62px;
  height: 62px;
  border-radius: 50%;
  background: #3249D7;
  color: #fff;
  font-size: 22px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`

const HeaderInfo = styled.div`
  flex: 1;
  h2 {
    font-size: 20px;
    font-weight: 700;
    color: #1a1a2e;
    margin: 0 0 4px;
  }
  p {
    font-size: 13px;
    color: #555;
    margin: 0 0 8px;
  }
`

const HeaderRight = styled.div`
  text-align: right;
  font-size: 12px;
  color: #888;
  white-space: nowrap;
  line-height: 1.8;
  .complete {
    color: #52c41a;
    font-weight: 600;
  }
`

const KPIRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 12px;
  margin-bottom: 16px;
`

const KPIBox = styled.div`
  background: rgba(255, 255, 255, 0.6);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  border: 1px solid rgba(255, 255, 255, 0.75);
  border-radius: 12px;
  padding: 14px 16px;
  box-shadow: 0 4px 16px rgba(50, 73, 215, 0.07), inset 0 1px 0 rgba(255,255,255,0.9);
  transition: transform 0.15s ease;
  &:hover { transform: translateY(-2px); }
`

const KPIBoxLabel = styled.div`
  font-size: 10px;
  font-weight: 700;
  color: #888;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  margin-bottom: 4px;
`

const KPIBig = styled.div`
  font-size: 32px;
  font-weight: 800;
  color: ${p => p.color || '#1a1a2e'};
  line-height: 1.1;
`

const KPISub = styled.div`
  font-size: 11px;
  color: #999;
  margin-top: 2px;
`

const ChartsRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 16px;
`

const ChartBox = styled.div`
  border: 1px solid #eef0fb;
  border-radius: 10px;
  padding: 14px 16px;
  background: #fff;
`

const ChartBoxLabel = styled.div`
  font-size: 10px;
  font-weight: 700;
  color: #888;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  margin-bottom: 8px;
`

const GaugeWrap = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
`

const AssessmentCard = styled.div`
  background: rgba(255,255,255,0.65);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(255,255,255,0.8);
  border-radius: 12px;
  padding: 14px 16px;
  margin-bottom: 12px;
  box-shadow: 0 2px 12px rgba(50,73,215,0.06);
`

const ScoreRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 12px;
  color: #555;
  margin-bottom: 4px;
  span:last-child { font-weight: 600; color: #1a1a2e; }
`

const CEFRBadge = styled.span`
  display: inline-block;
  padding: 2px 10px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 700;
  background: ${p => p.bg || '#eef0fb'};
  color: ${p => p.color || '#3249D7'};
  margin-left: 8px;
`

const GaugeCenter = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  pointer-events: none;
`

const GaugeScore = styled.div`
  font-size: 28px;
  font-weight: 800;
  color: #1a1a2e;
  line-height: 1;
`

const GaugeMax = styled.div`
  font-size: 12px;
  color: #aaa;
`

const GaugeAttrs = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  margin-top: 8px;
  width: 100%;
`

const AttrBox = styled.div`
  border: 1px solid #eef0fb;
  border-radius: 8px;
  padding: 6px 10px;
  text-align: center;
  font-size: 11px;
  color: #888;
  .val {
    font-weight: 700;
    font-size: 12px;
    color: ${p => p.valColor || '#1a1a2e'};
    display: block;
    margin-top: 2px;
  }
`

const BottomRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 12px;
`

const InfoBox = styled.div`
  border: 1px solid #eef0fb;
  border-radius: 10px;
  padding: 14px 16px;
  background: #fff;
`

const InfoBoxLabel = styled.div`
  font-size: 10px;
  font-weight: 700;
  color: #888;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  margin-bottom: 10px;
`

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 5px 0;
  border-bottom: 1px solid #f5f5f5;
  font-size: 12px;
  &:last-child { border-bottom: none; }
  .lbl { color: #999; }
  .val { color: #1a1a2e; font-weight: 500; text-align: right; max-width: 55%; word-break: break-word; }
`

const TimelineItem = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 14px;
  &:last-child { margin-bottom: 0; }
`

const TimelineDot = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${p => p.color || '#3249D7'};
  flex-shrink: 0;
  margin-top: 3px;
`

const TimelineContent = styled.div`
  .title { font-size: 13px; font-weight: 600; color: #1a1a2e; }
  .sub { font-size: 11px; color: #888; margin: 1px 0; }
  .marks { font-size: 12px; font-weight: 600; color: ${p => p.color || '#f59e0b'}; }
`

const SectionLabel = styled.div`
  font-size: 10px;
  font-weight: 700;
  color: #888;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  margin: 12px 0 6px;
  &:first-child { margin-top: 0; }
`

const MarkBarRow = styled.div`
  margin-bottom: 10px;
  .top { display: flex; justify-content: space-between; font-size: 11px; color: #555; margin-bottom: 4px; }
`

const WLPWrap = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`

const AttrRowItem = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  padding: 4px 0;
  border-bottom: 1px solid #f5f5f5;
  &:last-child { border-bottom: none; }
  .lbl { color: #888; }
  .val { font-weight: 600; color: ${p => p.valColor || '#52c41a'}; }
`

// ── Chart page styled components (tab-level) ──────────────────

const ChartsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-top: 28px;
  @media (max-width: 768px) { grid-template-columns: 1fr; }
`

const ChartCard = styled.div`
  background: #ffffff;
  border: 1px solid #e8ecf4;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.04);
`

const ChartTitle = styled.h4`
  font-size: 14px;
  font-weight: 600;
  color: #1a1a2e;
  margin: 0 0 12px;
`

const PIE_COLORS = ['#3249D7', '#f5222d', '#faad14', '#52c41a']

// ── Helpers ───────────────────────────────────────────────────

const getInitials = (profile) => {
  if (profile.full_name) {
    return profile.full_name.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase()
  }
  return `${(profile.first_name || '')[0] || ''}${(profile.last_name || '')[0] || ''}`.toUpperCase()
}

const marksColor = (v) => {
  if (v >= 80) return '#52c41a'
  if (v >= 60) return '#f59e0b'
  return '#f5222d'
}

const formatWLP = (val) => {
  if (!val) return []
  let arr = val
  if (!Array.isArray(arr)) {
    try { arr = JSON.parse(arr) } catch { return [String(val)] }
  }
  return arr.map(item => {
    if (typeof item === 'string') return item
    // Prefer human-readable names; never fall back to the raw *_id ObjectIds
    // (the backend already resolves these to names — this is just a guard).
    if (item && typeof item === 'object') return item.city_name || item.city || item.state_name || item.state || null
    return null
  }).filter(Boolean)
}

// ── Education level config ────────────────────────────────────

const EDU_LEVELS = [
  { key: 'tenth',   label: '10th', tags: ['tenth','TENTH','10th','Tenth','X','SSC'],                                                          fill: '#3249D7' },
  { key: 'twelfth', label: '12th', tags: ['twelfth','TWELFTH','12th','Twelth','XII','HSC','INTERMEDIATE','intermediate','Twelfth'],           fill: '#7c3aed' },
  { key: 'ug',      label: 'UG',   tags: ['graduation','ug','UG','GRADUATION','under_graduation','undergraduate','Graduation'],                fill: '#0891b2' },
  { key: 'pg',      label: 'PG',   tags: ['post_graduation','pg','PG','POST_GRADUATION','postgraduate','masters','Post_Graduation'],           fill: '#059669' },
]

// Map a free-form cc.degree string ("b.tech" / "Bachelor of Engineering" /
// "Master of Business Administration" / "M.E." / etc.) to one of the
// EDU_LEVELS keys. Returns null when the degree is a diploma / polytechnic /
// PhD — those don't have a slot in the 4-card layout and stay as a
// separate Current Course card.
const degreeToLevel = (degree) => {
  if (!degree) return null
  const d = String(degree).toLowerCase().trim()
  const compact = d.replace(/[.\s]/g, '')
  // PG checked first so "master" / "post graduate" win over a stray "b" prefix
  if (/^master\b|^post\s*graduate\b|^post-?grad\b/.test(d)) return 'pg'
  if (/^(mtech|me|msc|mba|mcom|mca|mpharm|ma|phd|mphil)/.test(compact)) return 'pg'
  if (/^bachelor\b|^under\s*graduate\b|^undergrad\b/.test(d))            return 'ug'
  if (/^(btech|be|bsc|bba|bcom|bca|bpharm|ba|barch)/.test(compact))      return 'ug'
  if (/^(12|twelfth|hsc|intermediate)\b/.test(d))                        return 'twelfth'
  if (/^(10|tenth|ssc)\b/.test(d))                                       return 'tenth'
  return null
}

// ── Rich Profile Modal Content ────────────────────────────────

const ProfileModalContent = ({ profile }) => {
  const ccMarks      = parseFloat(profile.average_marks) || 0
  // Which of the 4 standard levels (if any) does the student's *current*
  // course belong to. If we have a match, the current-course marks fold
  // into that level's card/bar instead of producing a duplicate
  // "Current Course" entry (e.g. a b.tech student in UG shouldn't render
  // both "UG 83%" and "b.tech 83%" — that's the same data).
  const currentLevelKey = degreeToLevel(profile.degree)

  const eduMap = EDU_LEVELS.map(lvl => {
    const entry = (profile.education_history || []).find(e => lvl.tags.includes(e.education_level))
    const histMarks = parseFloat(entry?.marks) || 0
    // For the level the student is currently at, prefer cc.average_marks —
    // it's the live snapshot; the education_profile row, if any, may be a
    // stale copy or an older record from a different institution.
    const marks = (lvl.key === currentLevelKey && ccMarks > 0) ? ccMarks : histMarks
    return { ...lvl, marks, isCurrent: lvl.key === currentLevelKey }
  })

  const tenthMarks   = eduMap.find(e => e.key === 'tenth')?.marks || 0
  // Kept name for compatibility with the existing placement-score formula —
  // semantically this is the *current course* average rather than a literal
  // diploma. When currentLevelKey is non-null the same value also lives in
  // eduMap so it isn't shown twice in the UI.
  const diplomaMarks = ccMarks
  // Show a stand-alone "Current Course" card / bar only when the current
  // course doesn't already fit into one of the 4 standard levels (e.g.
  // Diploma, Polytechnic, PhD).
  const showSeparateCurrent = !currentLevelKey && ccMarks > 0
  const arrears      = profile.no_of_arrears || 0
  const dobRaw       = profile.dob ? new Date(profile.dob) : null
  const dob          = dobRaw && !isNaN(dobRaw.getTime()) ? dobRaw : null
  const age          = dob ? Math.floor((Date.now() - dob) / (365.25 * 24 * 3600 * 1000)) : null

  const placementScore = Math.min(100, Math.round(
    diplomaMarks * 0.50
    + tenthMarks * 0.20
    + (arrears === 0 ? 20 : arrears <= 2 ? 10 : 0)
    + 5 + 5
  ))

  const academicData = [
    ...eduMap.filter(e => e.marks > 0).map(e => ({
      name: e.isCurrent && profile.degree ? profile.degree : e.label,
      value: e.marks,
      fill: e.fill,
    })),
    ...(showSeparateCurrent ? [{ name: profile.degree || 'Current Course', value: ccMarks, fill: '#52c41a' }] : []),
  ]

  const gaugeData = [
    { name: 'Score', value: placementScore,       fill: '#f59e0b' },
    { name: 'Rest',  value: 100 - placementScore, fill: '#f3f4f6' },
  ]

  const wlp = formatWLP(profile.preferred_job_location)
  const displayName = profile.full_name || `${profile.first_name || ''} ${profile.last_name || ''}`

  return (
    <ProfileWrap>
      {/* ── Header ── */}
      <ProfileHeader>
        <Avatar>{getInitials(profile)}</Avatar>
        <HeaderInfo>
          <h2>{displayName}</h2>
          <p>
            {[profile.department, profile.institute_campus_name,
              [profile.corr_city || profile.perm_city, profile.corr_state || profile.perm_state].filter(Boolean).join(', ')
            ].filter(Boolean).join(' • ')}
          </p>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {profile.degree   && <Tag color="blue">{profile.degree}</Tag>}
            <Tag color="green">Active</Tag>
            {arrears > 0
              ? <Tag color="orange">{arrears} Arrear{arrears > 1 ? 's' : ''}</Tag>
              : <Tag color="green">No Arrears</Tag>}
          </div>
        </HeaderInfo>
        <HeaderRight>
          <div>Student ID: {profile.id?.toString().substring(0, 8).toUpperCase()}</div>
          {profile.profile_url && <div className="complete">✓ Profile Complete</div>}
        </HeaderRight>
      </ProfileHeader>

      {/* ── KPI Cards ── */}
      <KPIRow>
        {eduMap.map(e => (
          <KPIBox key={e.key}>
            <KPIBoxLabel>{e.label} Marks</KPIBoxLabel>
            <KPIBig color={e.marks > 0 ? marksColor(e.marks) : '#ccc'}>
              {e.marks > 0 ? `${e.marks.toFixed(0)}%` : '—'}
            </KPIBig>
            <KPISub>
              {e.isCurrent && profile.degree
                ? `${profile.degree} (Current Course)`
                : (e.label === '10th' ? 'Board Exam' : e.label === '12th' ? 'Intermediate' : `${e.label} Degree`)}
            </KPISub>
          </KPIBox>
        ))}
        {showSeparateCurrent && (
          <KPIBox>
            <KPIBoxLabel>{profile.degree || 'Current Course'}</KPIBoxLabel>
            <KPIBig color={marksColor(ccMarks)}>{ccMarks.toFixed(0)}%</KPIBig>
            <KPISub>Current Course</KPISub>
          </KPIBox>
        )}
        <KPIBox>
          <KPIBoxLabel>Arrears</KPIBoxLabel>
          <KPIBig color={arrears === 0 ? '#52c41a' : '#f59e0b'}>{arrears}</KPIBig>
          <KPISub>Active backlogs</KPISub>
        </KPIBox>
        <KPIBox>
          <KPIBoxLabel>Age</KPIBoxLabel>
          <KPIBig color="#1a1a2e">{age ?? '—'}</KPIBig>
          <KPISub>
            {dob ? `DOB: ${dob.toISOString().split('T')[0].split('-').reverse().join('/')}` : '—'}
          </KPISub>
        </KPIBox>
      </KPIRow>

      {/* ── Charts ── */}
      <ChartsRow>
        <ChartBox>
          <ChartBoxLabel>Academic Performance</ChartBoxLabel>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={academicData} margin={{ top: 4, right: 10, left: -20, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis domain={[0, 100]} tick={{ fontSize: 10 }} unit="%" />
              <Tooltip formatter={v => `${v}%`} />
              <Bar dataKey="value" radius={[6, 6, 0, 0]} maxBarSize={60}>
                {academicData.map((entry, i) => (
                  <Cell key={i} fill={entry.fill} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartBox>

        <ChartBox>
          <ChartBoxLabel>Placement Readiness Score</ChartBoxLabel>
          <GaugeWrap>
            <div style={{ position: 'relative', width: '100%', height: 150 }}>
              <ResponsiveContainer width="100%" height={150}>
                <PieChart>
                  <Pie
                    data={gaugeData}
                    cx="50%"
                    cy="80%"
                    startAngle={180}
                    endAngle={0}
                    innerRadius={55}
                    outerRadius={75}
                    paddingAngle={0}
                    dataKey="value"
                  >
                    {gaugeData.map((entry, i) => (
                      <Cell key={i} fill={entry.fill} />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
              <GaugeCenter style={{ top: '62%' }}>
                <GaugeScore>{placementScore}</GaugeScore>
                <GaugeMax>/ 100</GaugeMax>
              </GaugeCenter>
            </div>

            <GaugeAttrs>
              <AttrBox valColor={arrears === 0 ? '#52c41a' : '#f59e0b'}>
                Arrears
                <span className="val">{arrears === 0 ? 'Clear' : `${arrears} Active`}</span>
              </AttrBox>
              <AttrBox valColor={profile.is_anywhere ? '#52c41a' : '#888'}>
                Anywhere
                <span className="val">{profile.is_anywhere ? 'Yes' : 'No'}</span>
              </AttrBox>
              <AttrBox valColor={profile.assessment_cefr ? '#52c41a' : '#888'}>
                OET / CEFR
                <span className="val">{profile.assessment_cefr || profile.practice_cefr || '—'}</span>
              </AttrBox>
              <AttrBox valColor={diplomaMarks >= 70 ? '#52c41a' : '#f59e0b'}>
                Marks Band
                <span className="val">{diplomaMarks >= 80 ? 'High' : diplomaMarks >= 60 ? 'Mid' : 'Low'}</span>
              </AttrBox>
            </GaugeAttrs>
          </GaugeWrap>
        </ChartBox>
      </ChartsRow>

      {/* ── Bottom Row ── */}
      <BottomRow>
        {/* Personal Info */}
        <InfoBox>
          <InfoBoxLabel>Personal Information</InfoBoxLabel>
          {[
            ['Email',    profile.primary_email],
            ['Phone',    profile.contact_number],
            ['Gender',   profile.gender ? profile.gender.charAt(0) + profile.gender.slice(1).toLowerCase() : null],
            ['City',     profile.corr_city || profile.perm_city],
            ['State',    profile.corr_state || profile.perm_state],
            ['Pincode',  profile.perm_post_code],
            ['Phys. Challenged', profile.is_physically_challenged ? 'Yes' : 'No'],
          ].map(([lbl, val]) => val != null ? (
            <InfoRow key={lbl}>
              <span className="lbl">{lbl}</span>
              <span className="val">{val}</span>
            </InfoRow>
          ) : null)}
        </InfoBox>

        {/* Education Timeline */}
        <InfoBox>
          <InfoBoxLabel>Education Timeline</InfoBoxLabel>
          {(profile.education_history || []).length > 0
            ? [...profile.education_history]
                .sort((a, b) => (b.ended_on || '') > (a.ended_on || '') ? 1 : -1)
                .map((e, i) => {
                  const m = parseFloat(e.marks)
                  const color = i === 0 ? '#3249D7' : '#52c41a'
                  return (
                    <TimelineItem key={i}>
                      <TimelineDot color={color} />
                      <TimelineContent color={marksColor(m)}>
                        <div className="title">
                          {e.education_level ? `${e.education_level.charAt(0).toUpperCase()}${e.education_level.slice(1)}` : '—'}
                          {e.degree ? ` – ${e.degree}` : ''}
                        </div>
                        <div className="sub">{e.institution_name || '—'}</div>
                        {!isNaN(m) && (
                          <div className="marks">
                            Marks: {m.toFixed(1)}%
                            {e.cumulative_type ? ` · ${e.cumulative_type}` : ''}
                          </div>
                        )}
                      </TimelineContent>
                    </TimelineItem>
                  )
                })
            : <div style={{ color: '#bbb', fontSize: 12 }}>No education history available</div>
          }
        </InfoBox>

        {/* Right panel */}
        <InfoBox>
          <SectionLabel>Marks Breakdown</SectionLabel>
          {[
            ...eduMap.filter(e => e.marks > 0).map(e => ({
              ...e,
              // If this level is the student's current course, label the bar
              // with the degree string (e.g. "B.Tech") instead of just "UG"
              // so the user sees which course it represents.
              label: e.isCurrent && profile.degree ? `${profile.degree} (${e.label})` : e.label,
            })),
            ...(showSeparateCurrent ? [{ key: 'current', label: profile.degree || 'Current Course', marks: ccMarks, fill: '#52c41a' }] : []),
          ].map(e => (
            <MarkBarRow key={e.key}>
              <div className="top">
                <span>{e.label}</span>
                <span style={{ color: marksColor(e.marks), fontWeight: 600 }}>
                  {e.marks.toFixed(0)}%
                </span>
              </div>
              <Progress
                percent={e.marks}
                showInfo={false}
                strokeColor={marksColor(e.marks)}
                trailColor="#eef0fb"
                size="small"
              />
            </MarkBarRow>
          ))}

          {wlp.length > 0 && (
            <>
              <SectionLabel>Work Preferences</SectionLabel>
              <WLPWrap>
                {wlp.map((loc, i) => (
                  <Tag key={i} style={{ borderRadius: 20, fontSize: 11 }}>
                    📍 {loc}
                  </Tag>
                ))}
              </WLPWrap>
            </>
          )}

          {(profile.skills || []).length > 0 && (() => {
            const SOURCE_COLOR = { Project: 'blue', Internship: 'purple', Course: 'green', Training: 'cyan', 'Work Experience': 'orange' }
            const grouped = (profile.skills || []).reduce((acc, s) => {
              const src = s.source || 'Other'
              if (!acc[src]) acc[src] = []
              acc[src].push(s.name)
              return acc
            }, {})
            return (
              <>
                <SectionLabel>Skills</SectionLabel>
                {Object.entries(grouped).map(([src, names]) => (
                  <div key={src} style={{ marginBottom: 8 }}>
                    <div style={{ fontSize: 10, color: '#888', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>{src}</div>
                    <WLPWrap>
                      {names.map((name, i) => (
                        <Tag key={i} color={SOURCE_COLOR[src] || 'default'} style={{ borderRadius: 20, fontSize: 11 }}>{name}</Tag>
                      ))}
                    </WLPWrap>
                  </div>
                ))}
              </>
            )
          })()}

          <SectionLabel>Profile Attributes</SectionLabel>
          {[
            ['Degree',       profile.degree,       '#3249D7'],
            ['Specialisation', profile.specialisation, '#3249D7'],
            ['Backlisted (Attend.)', profile.attendance_backlisted ? 'Yes' : 'No', profile.attendance_backlisted ? '#f5222d' : '#52c41a'],
            ['Backlisted (Conduct)', profile.conduct_backlisted    ? 'Yes' : 'No', profile.conduct_backlisted    ? '#f5222d' : '#52c41a'],
            ['Zero Arrears', arrears === 0 ? 'Yes' : 'No', arrears === 0 ? '#52c41a' : '#f59e0b'],
          ].filter(([, val]) => val != null).map(([lbl, val, c]) => (
            <AttrRowItem key={lbl} valColor={c}>
              <span className="lbl">{lbl}</span>
              <span className="val">{val}</span>
            </AttrRowItem>
          ))}
        </InfoBox>
      </BottomRow>
    </ProfileWrap>
  )
}

// ── Table columns ─────────────────────────────────────────────

const CEFR_META = {
  A1: { bg: '#fce4ec', color: '#c2185b' },
  A2: { bg: '#fff3e0', color: '#e65100' },
  B1: { bg: '#fffde7', color: '#f9a825' },
  B2: { bg: '#e8f5e9', color: '#2e7d32' },
  C1: { bg: '#e3f2fd', color: '#1565c0' },
  C2: { bg: '#ede7f6', color: '#6a1b9a' },
}

const ASSESSMENT_TYPE_COLOR = {
  communication: 'blue',
  aptitude: 'orange',
  behavioral: 'purple',
}

const typeColor = t => ASSESSMENT_TYPE_COLOR[(t || '').toLowerCase()] || 'default'
const isCommunication = t => /comm/i.test(t || '')
const isAptitude = t => /apt/i.test(t || '')

const fmtDate = iso => {
  if (!iso) return '—'
  const d = new Date(iso)
  return isNaN(d) ? '—' : d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
}

// ── Filter popup constants ─────────────────────────────────────

const toArr = v => Array.isArray(v) ? v : (v != null && v !== '' ? [v] : [])

const GENDER_OPTIONS = [
  { value: 'MALE',              label: 'Male' },
  { value: 'FEMALE',            label: 'Female' },
  { value: 'OTHER',             label: 'Other' },
  { value: 'TRANSGENDER',       label: 'Transgender' },
  { value: 'PREFER_NOT_TO_SAY', label: 'Prefer Not To Say' },
]
const GENDER_DISPLAY = Object.fromEntries(GENDER_OPTIONS.map(o => [o.value, o.label]))

const ASSESSED_OPTIONS = [
  { value: 'yes', label: 'Assessed' },
  { value: 'no',  label: 'Not Assessed' },
]

const FILTER_CATS = [
  { key: 'college',    label: 'College' },
  { key: 'degree',     label: 'Degree' },
  { key: 'dept',       label: 'Department' },
  { key: 'gender',     label: 'Gender' },
  { key: 'state',      label: 'State' },
  { key: 'city',       label: 'City' },
  { key: 'assessed',   label: 'Assessment' },
  { key: 'minMarks',   label: 'Min Marks (%)' },
  { key: 'maxArrears', label: 'Max Arrears' },
  { key: 'skill',      label: 'Skill' },
]

const EMPTY_PENDING = {
  college: [], degree: [], dept: [], gender: [], state: [], city: [], skill: [],
  assessed: undefined, minMarks: undefined, maxArrears: undefined,
}

// ── Table columns ──────────────────────────────────────────────

const columns = (onViewProfile, onViewAssessments, allEmails = []) => [
  {
    title: 'Name',
    key: 'name',
    width: 150,
    render: (_, r) => (
      <span
        style={{ color: '#3249D7', cursor: 'pointer', fontWeight: 500 }}
        onClick={() => onViewProfile(r.id)}
      >
        {r.full_name || `${r.first_name || ''} ${r.last_name || ''}`}
      </span>
    ),
  },
  {
    title: (
      <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
        Email
        <AntTooltip title="Copy all visible emails">
          <CopyOutlined
            style={{ cursor: 'pointer', color: '#3249D7', fontSize: 12 }}
            onClick={e => {
              e.stopPropagation()
              const txt = allEmails.join('\n')
              navigator.clipboard.writeText(txt).then(() => message.success(`${allEmails.length} emails copied`))
            }}
          />
        </AntTooltip>
      </span>
    ),
    key: 'email', width: 180,
    render: (_, r) => r.primary_email ? (
      <Typography.Text
        copyable={{ tooltips: ['Copy email', 'Copied!'] }}
        ellipsis={{ tooltip: r.primary_email }}
        style={{ maxWidth: 148, fontSize: 13 }}
      >
        {r.primary_email}
      </Typography.Text>
    ) : '—',
  },
  { title: 'Phone', dataIndex: 'contact_number', key: 'phone', width: 130 },
  { title: 'Department', dataIndex: 'department', key: 'dept', ellipsis: true, width: 140 },
  { title: 'Degree', dataIndex: 'degree', key: 'degree', ellipsis: true, width: 120 },
  {
    title: 'Skills',
    key: 'skills',
    width: 120,
    sorter: true,
    render: (_, r) => {
      const raw = r.skills_json
      const skills = Array.isArray(raw) ? raw : (typeof raw === 'string' ? (() => { try { return JSON.parse(raw) } catch { return [] } })() : [])
      if (!skills.length) return <span style={{ color: '#ccc' }}>—</span>
      const shown = skills.slice(0, 1)
      const rest  = skills.slice(1)
      const popContent = (
        <div style={{ maxWidth: 260 }}>
          {skills.map((sk, i) => (
            <div key={i} style={{ padding: '3px 0', borderBottom: i < skills.length - 1 ? '1px solid #f0f0f0' : 'none' }}>
              <span style={{ color: '#aaa', marginRight: 6, fontSize: 11 }}>{i + 1})</span>
              <strong style={{ fontSize: 12 }}>{sk.name}</strong>
              <span style={{ color: '#888', fontSize: 11 }}> — {sk.src}</span>
            </div>
          ))}
        </div>
      )
      return (
        <div style={{ display: 'flex', flexWrap: 'nowrap', gap: 3, alignItems: 'center' }}>
          {shown.map((sk, i) => (
            <Tag key={i} color="geekblue" style={{ fontSize: 11, margin: 0, maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {sk.name}
            </Tag>
          ))}
          {rest.length > 0 && (
            <Popover content={popContent} title={`All Skills (${skills.length})`} trigger="hover" placement="right" overlayInnerStyle={{ background: '#fffde7' }}>
              <Tag style={{ cursor: 'pointer', fontSize: 11, margin: 0, color: '#3249D7', borderColor: '#3249D7' }}>···</Tag>
            </Popover>
          )}
        </div>
      )
    },
  },
  {
    title: 'Assessment',
    dataIndex: 'assessment_count',
    key: 'assessment',
    width: 100,
    render: (v, r) => {
      const count = Number(v)
      return count > 0
        ? (
          <span
            style={{ cursor: 'pointer', display: 'inline-block' }}
            onClick={e => { e.stopPropagation(); onViewAssessments(r) }}
          >
            <Tag color="purple">Assessed ({count})</Tag>
          </span>
        )
        : <Tag color="default">Not Assessed</Tag>
    },
  },
  {
    title: 'Location', key: 'location', width: 130, ellipsis: true,
    render: (_, r) => {
      const parts = [r.corr_city, r.corr_state].filter(Boolean)
      if (!parts.length) return <span style={{ color: '#ccc' }}>—</span>
      const text = parts.join(', ')
      return (
        <AntTooltip title={text}>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block', maxWidth: 118 }}>
            {text}
          </span>
        </AntTooltip>
      )
    },
  },
  { title: 'College', dataIndex: 'institute_campus_name', key: 'college', ellipsis: true, width: 150 },
  {
    title: 'Marks',
    dataIndex: 'average_marks',
    key: 'marks',
    width: 72,
    render: v => v != null ? `${parseFloat(v).toFixed(1)}%` : '—',
    // Server-driven sort — client-side comparator would only reorder the
    // current page. handleTableChange forwards columnKey + order to the
    // /meta-dashboard/students endpoint as sortBy + sortOrder.
    sorter: true,
  },
  {
    title: 'Arrears',
    dataIndex: 'no_of_arrears',
    key: 'arrears',
    width: 72,
    render: v => (
      <Tag color={v === 0 ? 'green' : v <= 2 ? 'orange' : 'red'}>{v ?? '—'}</Tag>
    ),
  },
  {
    title: 'Gender',
    dataIndex: 'gender',
    key: 'gender',
    width: 80,
    render: v => v ? v.charAt(0) + v.slice(1).toLowerCase() : '—',
  },
]

// ── Main component ────────────────────────────────────────────

const StudentTab = ({
  students,
  studentsLoading,
  studentSummary,
  studentProfile,
  studentProfileLoading,
  filterDepartments,
  filterStates,
  filterDegrees,
  filterColleges,
  filterSkills,
  filterCities,
  fetchStudents,
  fetchStudentSummary,
  fetchStudentProfile,
  fetchStudentAssessments,
  studentAssessments,
  studentAssessmentsLoading,
  fetchFilterOptions,
  fetchStudentFilterOptions,
  fetchFilterSkills,
  fetchFilterDepartments,
  fetchFilterDegrees,
  fetchFilterStates,
  fetchFilterColleges,
  fetchFilterCities,
}) => {
  const [filters, setFilters]             = useState({ page: 1, limit: 10 })
  const [profileOpen, setProfileOpen]     = useState(false)
  const [searchText, setSearchText]       = useState('')
  const [assessmentOpen, setAssessmentOpen] = useState(false)
  const [assessmentStudent, setAssessmentStudent] = useState(null)
  const [filterOpen, setFilterOpen]       = useState(false)
  const [activeCat, setActiveCat]         = useState('college')
  const [catSearch, setCatSearch]         = useState('')
  const [pendingFilters, setPendingFilters] = useState(EMPTY_PENDING)
  // Stores { [collegeId]: collegeName } captured at selection time so the
  // active filter chip can display the name even after filterColleges is
  // overwritten by a subsequent search or re-fetch.
  const [collegeLabels, setCollegeLabels] = useState({})
  const searchTimer = useRef(null)
  const studentsTableRef = useRef(null)
  const tableWrapRef = useRef(null)

  const scrollTable = (delta) => {
    const el = tableWrapRef.current?.querySelector('.ant-table-content')
           || tableWrapRef.current?.querySelector('.ant-table-body')
    if (el) el.scrollLeft += delta
  }

  // One debounce timer per dropdown — all server-side searches with
  // identical semantics (350ms debounce, empty input returns default
  // alpha page). Each Select sets filterOption={false} so the rendered
  // options come straight from the API response.
  const skillSearchTimer   = useRef(null)
  const deptSearchTimer    = useRef(null)
  const degreeSearchTimer  = useRef(null)
  const stateSearchTimer   = useRef(null)
  const collegeSearchTimer = useRef(null)
  const citySearchTimer    = useRef(null)

  const debouncedSearch = (timerRef, fn) => (value) => {
    clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => { if (typeof fn === 'function') fn(value) }, 350)
  }

  // Skill is global; the other four optionally scope to the currently
  // selected college so the search results stay relevant to that college.
  const handleSkillSearch   = debouncedSearch(skillSearchTimer,   fetchFilterSkills)
  const handleDeptSearch    = debouncedSearch(deptSearchTimer,    (s) => fetchFilterDepartments({ search: s, college: filters.college }))
  const handleDegreeSearch  = debouncedSearch(degreeSearchTimer,  (s) => fetchFilterDegrees({ search: s, college: filters.college }))
  const handleStateSearch   = debouncedSearch(stateSearchTimer,   fetchFilterStates)
  const handleCollegeSearch = debouncedSearch(collegeSearchTimer, fetchFilterColleges)
  const handleCitySearch    = debouncedSearch(citySearchTimer,    fetchFilterCities)

  useEffect(() => {
    fetchStudents(filters)
    fetchStudentSummary()
  }, [])

  // Refetch the Degree + Department dropdown options whenever the College
  // filter changes so they only list values that exist at the selected
  // college. We deliberately do NOT clear the user's dept/degree picks
  // here — handleApplyFilter sets college and degree/dept together, and
  // clearing them would wipe fresh picks the user just submitted in the
  // same action. If a pick is no longer valid at the new college the
  // server-side query simply returns no rows.
  const prevCollegeRef = useRef(filters.college)
  useEffect(() => {
    if (prevCollegeRef.current === filters.college) return
    prevCollegeRef.current = filters.college
    if (typeof fetchFilterOptions === 'function') {
      fetchFilterOptions({ college: filters.college })
    }
  }, [filters.college, fetchFilterOptions])

  // Google-Sheets cascading: refire the combined filter-options endpoint
  // as the user picks values INSIDE the filter modal (pendingFilters),
  // not only after Apply (filters). The dropdowns narrow on-the-fly so
  // selecting a college immediately shrinks the Degree / Department
  // dropdowns without having to close + reopen the modal.
  //
  // Keyed on a JSON stringify of the multi-select pending filters so
  // React only refires when their content (not identity) changes.
  const cascadingKey = JSON.stringify({
    college:      pendingFilters.college      || null,
    degree:       pendingFilters.degree       || null,
    dept:         pendingFilters.dept         || null,
    gender:       pendingFilters.gender       || null,
    state:        pendingFilters.state        || null,
    city:         pendingFilters.city         || null,
    skill:        pendingFilters.skill        || null,
    assessed:     pendingFilters.assessed     || null,
    profileReady: pendingFilters.profileReady || null,
    minMarks:     pendingFilters.minMarks     || null,
    maxArrears:   pendingFilters.maxArrears   || null,
  })
  useEffect(() => {
    // Only fire while the modal is open — otherwise the cascading
    // response would silently overwrite the global filter slots that
    // other tabs (Institute / Corporate 360) also read from.
    if (!filterOpen) return
    if (typeof fetchStudentFilterOptions === 'function') {
      // Pass activeCat so the cascading response doesn't overwrite the
      // currently-browsed category's list. Otherwise: search "sailesh" →
      // pick a college → cascading fires → server-search results vanish.
      fetchStudentFilterOptions(JSON.parse(cascadingKey), activeCat)
    }
  }, [filterOpen, cascadingKey, fetchStudentFilterOptions, activeCat])

  const handleFilter = () => {
    const f = { ...filters, page: 1 }
    setFilters(f)
    fetchStudents(f)
  }

  const handleReset = () => {
    const f = { page: 1, limit: 10 }
    setFilters(f)
    setSearchText('')
    fetchStudents(f)
    fetchStudentSummary()
  }

  const handleRemoveFilter = (key) => {
    const f = { ...filters, [key]: undefined, page: 1 }
    setFilters(f)
    fetchStudents(f)
  }

  const handleOpenFilter = () => {
    setPendingFilters({
      college:    toArr(filters.college).map(String),
      degree:     toArr(filters.degree),
      dept:       toArr(filters.dept),
      gender:     toArr(filters.gender),
      state:      toArr(filters.state),
      city:       toArr(filters.city),
      assessed:   filters.assessed,
      minMarks:   filters.minMarks,
      maxArrears: filters.maxArrears,
      skill:      toArr(filters.skill),
    })
    setActiveCat('college')
    setCatSearch('')
    setFilterOpen(true)
  }

  const handleApplyFilter = () => {
    const f = {
      ...filters,
      college:    pendingFilters.college.length    ? pendingFilters.college    : undefined,
      degree:     pendingFilters.degree.length     ? pendingFilters.degree     : undefined,
      dept:       pendingFilters.dept.length       ? pendingFilters.dept       : undefined,
      gender:     pendingFilters.gender.length     ? pendingFilters.gender     : undefined,
      state:      pendingFilters.state.length      ? pendingFilters.state      : undefined,
      city:       pendingFilters.city.length        ? pendingFilters.city       : undefined,
      assessed:   pendingFilters.assessed,
      minMarks:   pendingFilters.minMarks,
      maxArrears: pendingFilters.maxArrears != null ? pendingFilters.maxArrears : undefined,
      skill:      pendingFilters.skill.length       ? pendingFilters.skill      : undefined,
      page: 1,
    }
    setFilters(f)
    fetchStudents(f)
    setFilterOpen(false)
  }

  const toggleCheck = (key, val, label) => {
    if (key === 'assessed') {
      setPendingFilters(pf => ({ ...pf, [key]: pf[key] === val ? undefined : val }))
      return
    }
    if (key === 'college' && label) {
      setCollegeLabels(prev => ({ ...prev, [String(val)]: label }))
    }
    setPendingFilters(pf => {
      const cur = pf[key] || []
      return { ...pf, [key]: cur.includes(val) ? cur.filter(v => v !== val) : [...cur, val] }
    })
  }

  const catCount = (key) => {
    const v = pendingFilters[key]
    if (key === 'assessed')   return v != null ? 1 : 0
    if (key === 'minMarks')   return v > 0 ? 1 : 0
    if (key === 'maxArrears') return v != null ? 1 : 0
    return Array.isArray(v) ? v.length : 0
  }

  const getCatOptions = (key) => {
    // Defensive filters: the upstream Redux slot should already be of
    // the right shape, but if anything ever bypasses the cascading
    // thunk's type guard, we don't want filterColleges objects to render
    // inside filterDepartments etc. Each switch arm explicitly drops
    // values that don't match the expected primitive shape.
    const onlyStr = (arr) => (arr || []).filter(v => typeof v === 'string' && v.length > 0)
    const onlyCollege = (arr) => (arr || []).filter(v => v && typeof v === 'object' && v.id != null && typeof v.name === 'string')
    const opts = (() => {
      switch (key) {
        case 'college':  return onlyCollege(filterColleges).map(c => ({ value: String(c.id), label: c.name }))
        case 'degree':   return onlyStr(filterDegrees).map(d => ({ value: d, label: d }))
        case 'dept':     return onlyStr(filterDepartments).map(d => ({ value: d, label: d }))
        case 'gender':   return GENDER_OPTIONS
        case 'state':    return onlyStr(filterStates).map(s => ({ value: s, label: s }))
        case 'city':     return onlyStr(filterCities).map(c => ({ value: c, label: c }))
        case 'assessed': return ASSESSED_OPTIONS
        case 'skill':    return onlyStr(filterSkills).map(s => ({ value: s, label: s }))
        default:         return []
      }
    })()
    if (!catSearch) return opts
    const q = catSearch.toLowerCase()
    return opts.filter(o => o.label.toLowerCase().includes(q))
  }

  const handleCatSearch = (val) => {
    setCatSearch(val)
    if      (activeCat === 'college') handleCollegeSearch(val)
    else if (activeCat === 'skill')   handleSkillSearch(val)
    else if (activeCat === 'dept')    handleDeptSearch(val)
    else if (activeCat === 'degree')  handleDegreeSearch(val)
    else if (activeCat === 'state')   handleStateSearch(val)
    else if (activeCat === 'city')    handleCitySearch(val)
  }

  const activeChips = [
    { key: 'college',    vals: toArr(filters.college), getLabel: v => collegeLabels[String(v)] || filterColleges?.find(c => String(c.id) === String(v))?.name || v, catLabel: 'College' },
    { key: 'degree',     vals: toArr(filters.degree),  getLabel: v => v, catLabel: 'Degree' },
    { key: 'dept',       vals: toArr(filters.dept),    getLabel: v => v, catLabel: 'Dept' },
    { key: 'gender',     vals: toArr(filters.gender),  getLabel: v => GENDER_DISPLAY[v] || v, catLabel: 'Gender' },
    { key: 'state',      vals: toArr(filters.state),   getLabel: v => v, catLabel: 'State' },
    { key: 'city',       vals: toArr(filters.city),  getLabel: v => v, catLabel: 'City' },
    { key: 'assessed',   vals: filters.assessed ? [filters.assessed] : [], getLabel: v => v === 'yes' ? 'Assessed' : 'Not Assessed', catLabel: 'Assessment' },
    { key: 'minMarks',   vals: filters.minMarks > 0 ? [filters.minMarks] : [], getLabel: v => `≥ ${v}%`, catLabel: 'Marks' },
    { key: 'maxArrears', vals: filters.maxArrears != null ? [filters.maxArrears] : [], getLabel: v => `≤ ${v}`, catLabel: 'Arrears' },
    { key: 'skill',      vals: toArr(filters.skill), getLabel: v => v, catLabel: 'Skill' },
  ].filter(c => c.vals.length > 0)

  const handleKPIClick = (filterKey, filterVal) => {
    const isActive = filters[filterKey] === filterVal
    const newFilters = { ...filters, page: 1 }
    if (isActive) {
      delete newFilters[filterKey]
    } else {
      newFilters[filterKey] = filterVal
    }
    setFilters(newFilters)
    fetchStudents(newFilters)
  }

  const handleViewProfile = (id) => {
    fetchStudentProfile(id)
    setProfileOpen(true)
  }

  const handleViewAssessments = (student) => {
    setAssessmentStudent(student)
    fetchStudentAssessments(student.id)
    setAssessmentOpen(true)
  }

  const handleTableChange = (_pagination, _tableFilters, sorter) => {
    const sortBy = sorter && sorter.order ? sorter.columnKey : undefined
    const sortOrder = sorter && sorter.order === 'ascend' ? 'asc'
                    : sorter && sorter.order === 'descend' ? 'desc'
                    : undefined
    const f = { ...filters, sortBy, sortOrder }
    setFilters(f)
    fetchStudents(f)
  }

  const handlePaginationChange = (page, pageSize) => {
    const f = { ...filters, page, limit: pageSize }
    setFilters(f)
    fetchStudents(f)
  }

  const handleSearchChange = (e) => {
    const val = e.target.value
    setSearchText(val)
    clearTimeout(searchTimer.current)
    searchTimer.current = setTimeout(() => {
      const f = { ...filters, search: val || undefined, page: 1 }
      setFilters(f)
      fetchStudents(f)
    }, 350)
  }

  const data = students?.data || []
  const total = students?.total || 0
  const ss = studentSummary || {}

  const deptChartData = useMemo(() => {
    // Prefer summary dept_breakdown (all matching students, not just current page)
    if (Array.isArray(ss.dept_breakdown) && ss.dept_breakdown.length > 0) {
      return ss.dept_breakdown.map(d => ({
        dept: d.dept && d.dept.length > 14 ? d.dept.substring(0, 12) + '…' : (d.dept || '—'),
        Students: d.student_count,
        'Avg Marks': d.avg_marks != null ? parseFloat(d.avg_marks) : 0,
      }))
    }
    // Fallback: derive from current page data
    const map = {}
    data.forEach(s => {
      const d = s.department
      if (!d) return
      if (!map[d]) map[d] = { count: 0, total: 0, n: 0 }
      map[d].count++
      if (s.average_marks != null) {
        map[d].total += parseFloat(s.average_marks)
        map[d].n++
      }
    })
    return Object.entries(map)
      .map(([dept, v]) => ({
        dept: dept.length > 14 ? dept.substring(0, 12) + '…' : dept,
        Students: v.count,
        'Avg Marks': v.n > 0 ? parseFloat((v.total / v.n).toFixed(1)) : 0,
      }))
      .sort((a, b) => b.Students - a.Students)
      .slice(0, 10)
  }, [data, ss])

  const genderChartData = useMemo(() => {
    // Use global summary counts so it reflects all students, not just current page
    if (ss.total_students > 0 && (ss.male_count != null || ss.female_count != null)) {
      const items = [
        { name: 'Male',   value: ss.male_count   || 0 },
        { name: 'Female', value: ss.female_count  || 0 },
      ]
      const other = ss.total_students - (ss.male_count || 0) - (ss.female_count || 0)
      if (other > 0) items.push({ name: 'Other', value: other })
      return items.filter(g => g.value > 0)
    }
    // Fallback: derive from current page data
    const counts = {}
    data.forEach(s => {
      const g = s.gender
      if (!g) return
      counts[g] = (counts[g] || 0) + 1
    })
    return Object.entries(counts).map(([name, value]) => ({
      name: name.charAt(0) + name.slice(1).toLowerCase(),
      value,
    }))
  }, [data, ss])

  const maleCount = (ss.total_students != null && ss.female_count != null) ? ss.total_students - ss.female_count : null

  const summaryKPIs = [
    { icon: <TeamOutlined />,         label: 'Total Students',      value: ss.total_students != null ? ss.total_students.toLocaleString() : '—',                                           bg: '#eef0fb', color: '#3249D7', scrollToTable: true },
      // subline: (ss.female_count != null && maleCount != null) ? `Female: ${ss.female_count.toLocaleString()}  Male: ${maleCount.toLocaleString()}` : null },
    { icon: <BarChartOutlined />,     label: 'Avg Marks',           value: ss.avg_marks != null ? `${ss.avg_marks}%` : '—',                                                               bg: '#e8f5e9', color: '#2e7d32' },
    { icon: <TrophyOutlined />,       label: 'Zero Arrears',        value: ss.zero_arrears != null ? `${ss.zero_arrears.toLocaleString()} (${ss.zero_arrears_pct}%)` : '—',               bg: '#fff3e0', color: '#e65100', filterKey: 'maxArrears', filterVal: 0, valueFontSize: 15 },
    { icon: <StarOutlined />,         label: 'High Performers',     value: ss.high_performers != null ? ss.high_performers.toLocaleString() : '—',                                         bg: '#fffde7', color: '#f59e0b', filterKey: 'minMarks',   filterVal: 80 },
    { icon: <CheckCircleOutlined />,  label: 'Assessed',            value: ss.assessed_count != null ? ss.assessed_count.toLocaleString() : '—',                                           bg: '#e8f5e9', color: '#059669', filterKey: 'assessed',   filterVal: 'yes' },
    { icon: <UserOutlined />,         label: 'Profile Ready',       value: ss.profile_ready != null ? ss.profile_ready.toLocaleString() : '—',                                            bg: '#f3e8ff', color: '#7c3aed', filterKey: 'profileReady', filterVal: 'yes' },
  ]

  return (
    <div>
      <SummaryGrid>
        {summaryKPIs.map((k, i) => {
          const active = k.filterKey !== undefined && filters[k.filterKey] === k.filterVal
          const tip = k.filterKey ? (active ? 'Click to clear filter' : `Filter by ${k.label}`) : k.scrollToTable ? 'Scroll to students list' : null
          return (
            <AntTooltip key={i} title={tip}>
              <SummaryKPICard
                $clickable={!!(k.filterKey || k.scrollToTable)}
                $active={active}
                $color={k.color}
                onClick={() => {
                  if (k.filterKey) handleKPIClick(k.filterKey, k.filterVal)
                  else if (k.scrollToTable) studentsTableRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                }}
              >
                <SKPIIcon bg={k.bg} color={k.color}>{k.icon}</SKPIIcon>
                <div>
                  <SKPIValue style={{ ...(active ? { color: k.color } : {}), ...(k.valueFontSize ? { fontSize: k.valueFontSize } : {}) }}>{k.value}</SKPIValue>
                  <SKPILabel>{k.label}{active ? ' ×' : ''}</SKPILabel>
                  {k.subline && <div style={{ fontSize: 10, color: '#888', marginTop: 3, lineHeight: 1.3 }}>{k.subline}</div>}
                </div>
              </SummaryKPICard>
            </AntTooltip>
          )
        })}
      </SummaryGrid>

      <div ref={studentsTableRef} />

      {/* Search + Filter bar with inline pagination */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: activeChips.length ? 8 : 12 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <Input
            prefix={<SearchOutlined style={{ color: '#bbb' }} />}
            placeholder="Search by name / email / phone…"
            value={searchText}
            onChange={handleSearchChange}
            allowClear
            onClear={() => { setSearchText(''); const f = { ...filters, search: undefined, page: 1 }; setFilters(f); fetchStudents(f) }}
            style={{ width: 300 }}
          />
          <Button
            icon={<FilterOutlined />}
            onClick={handleOpenFilter}
            style={activeChips.length ? { borderColor: '#3249D7', color: '#3249D7', fontWeight: 600 } : {}}
          >
            Filter{activeChips.length > 0 ? ` (${activeChips.length})` : ''}
          </Button>
          {activeChips.length > 0 && (
            <Button onClick={handleReset} style={{ color: '#999' }}>Reset all</Button>
          )}
        </div>
        <Pagination
          size="small"
          current={students?.page || 1}
          pageSize={students?.limit || 10}
          total={students?.total || 0}
          showTotal={(t, range) => `${range[0]}–${range[1]} of ${t.toLocaleString()} students`}
          showSizeChanger
          pageSizeOptions={['10', '25', '50', '100']}
          onChange={handlePaginationChange}
        />
      </div>

      {/* Active filter chips */}
      {activeChips.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 12 }}>
          {activeChips.map(chip => (
            <Tag
              key={chip.key}
              closable
              onClose={() => handleRemoveFilter(chip.key)}
              color="blue"
              style={{ fontSize: 11, padding: '2px 8px' }}
            >
              {chip.vals.length === 1
                ? `${chip.catLabel}: ${chip.getLabel(chip.vals[0])}`
                : `${chip.catLabel} (${chip.vals.length})`}
            </Tag>
          ))}
        </div>
      )}

      {/* Filter modal */}
      <Modal
        open={filterOpen}
        onCancel={() => setFilterOpen(false)}
        footer={null}
        width={700}
        title={<span style={{ fontSize: 14, fontWeight: 600 }}>Filter Students</span>}
        styles={{ body: { padding: 0 } }}
      >
        <div style={{ display: 'flex', height: 460 }}>
          {/* Left sidebar — categories */}
          <div style={{ width: 168, borderRight: '1px solid #f0f0f0', overflowY: 'auto', flexShrink: 0 }}>
            {FILTER_CATS.map(cat => {
              const cnt = catCount(cat.key)
              return (
                <div
                  key={cat.key}
                  onClick={() => { setActiveCat(cat.key); setCatSearch('') }}
                  style={{
                    padding: '11px 14px',
                    cursor: 'pointer',
                    background: activeCat === cat.key ? '#f0f2ff' : 'white',
                    borderLeft: `3px solid ${activeCat === cat.key ? '#3249D7' : 'transparent'}`,
                    color: activeCat === cat.key ? '#3249D7' : '#444',
                    fontWeight: activeCat === cat.key ? 600 : 400,
                    fontSize: 13,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderBottom: '1px solid #f7f7f7',
                    userSelect: 'none',
                  }}
                >
                  {cat.label}
                  {cnt > 0 && (
                    <span style={{ background: '#3249D7', color: '#fff', borderRadius: 10, fontSize: 10, padding: '1px 6px', fontWeight: 700, minWidth: 18, textAlign: 'center' }}>
                      {cnt}
                    </span>
                  )}
                </div>
              )
            })}
          </div>

          {/* Right panel — options */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {/* Search (hidden for numeric inputs) */}
            {activeCat !== 'minMarks' && activeCat !== 'maxArrears' && (
              <div style={{ padding: '10px 14px', borderBottom: '1px solid #f0f0f0' }}>
                <Input
                  size="small"
                  prefix={<SearchOutlined style={{ color: '#bbb' }} />}
                  placeholder="Search…"
                  value={catSearch}
                  onChange={e => handleCatSearch(e.target.value)}
                  allowClear
                />
              </div>
            )}
            {/* Select-all / Clear toolbar — applies to the search-filtered
                set, hidden for numeric inputs and single-select categories. */}
            {activeCat !== 'minMarks' && activeCat !== 'maxArrears' && activeCat !== 'assessed' && (() => {
              const visible       = getCatOptions(activeCat)
              const selectedSet   = new Set(pendingFilters[activeCat] || [])
              const visibleValues = visible.map(o => o.value)
              const visibleSelectedCount = visibleValues.filter(v => selectedSet.has(v)).length
              const allVisibleSelected   = visible.length > 0 && visibleSelectedCount === visible.length
              const someVisibleSelected  = visibleSelectedCount > 0 && !allVisibleSelected
              const onToggleAll = () => {
                setPendingFilters(pf => {
                  const current = new Set(pf[activeCat] || [])
                  if (allVisibleSelected) {
                    visibleValues.forEach(v => current.delete(v))
                  } else {
                    visibleValues.forEach(v => current.add(v))
                  }
                  return { ...pf, [activeCat]: Array.from(current) }
                })
              }
              return visible.length === 0 ? null : (
                <div style={{ padding: '6px 14px', borderBottom: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', gap: 10 }}>
                  <Checkbox
                    indeterminate={someVisibleSelected}
                    checked={allVisibleSelected}
                    onChange={onToggleAll}
                  >
                    <span style={{ fontSize: 12.5, fontWeight: 500 }}>
                      Select all ({visible.length})
                    </span>
                  </Checkbox>
                  {visibleSelectedCount > 0 && (
                    <Button
                      type="link"
                      size="small"
                      style={{ marginLeft: 'auto', padding: 0, fontSize: 12 }}
                      onClick={() => setPendingFilters(pf => ({ ...pf, [activeCat]: [] }))}
                    >
                      Clear ({visibleSelectedCount})
                    </Button>
                  )}
                </div>
              )
            })()}
            <div style={{ flex: 1, overflowY: 'auto', padding: '10px 14px' }}>
              {activeCat === 'minMarks' ? (
                <div>
                  <div style={{ marginBottom: 8, color: '#888', fontSize: 12 }}>Show students with marks at or above this value</div>
                  <InputNumber
                    min={0} max={100} placeholder="e.g. 60"
                    value={pendingFilters.minMarks}
                    onChange={v => setPendingFilters(pf => ({ ...pf, minMarks: v }))}
                    style={{ width: '100%' }}
                    addonAfter="%"
                  />
                </div>
              ) : activeCat === 'maxArrears' ? (
                <div>
                  <div style={{ marginBottom: 8, color: '#888', fontSize: 12 }}>Show students with this many arrears or fewer</div>
                  <InputNumber
                    min={0} max={20} placeholder="e.g. 2"
                    value={pendingFilters.maxArrears}
                    onChange={v => setPendingFilters(pf => ({ ...pf, maxArrears: v }))}
                    style={{ width: '100%' }}
                  />
                </div>
              ) : getCatOptions(activeCat).length === 0 ? (
                <div style={{ color: '#bbb', fontSize: 13, padding: '16px 0' }}>No options found</div>
              ) : (
                getCatOptions(activeCat).map(opt => {
                  const isSingle = activeCat === 'assessed'
                  const checked  = isSingle
                    ? pendingFilters[activeCat] === opt.value
                    : (pendingFilters[activeCat] || []).includes(opt.value)
                  return (
                    <div
                      key={opt.value}
                      style={{
                        padding: '7px 4px',
                        borderBottom: '1px solid #f7f7f7',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        cursor: 'pointer',
                        borderRadius: 4,
                        background: checked ? '#f0f2ff' : 'transparent',
                      }}
                    >
                      <Checkbox checked={checked} onChange={() => toggleCheck(activeCat, opt.value, opt.label)}>
                        <span style={{ fontSize: 13 }}>{opt.label}</span>
                      </Checkbox>
                    </div>
                  )
                })
              )}
            </div>
          </div>
        </div>

        {/* Modal footer */}
        <div style={{ padding: '12px 16px', borderTop: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Button size="small" onClick={() => setPendingFilters(EMPTY_PENDING)} style={{ color: '#aaa' }}>
            Clear all
          </Button>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button onClick={() => setFilterOpen(false)}>Cancel</Button>
            <Button type="primary" onClick={handleApplyFilter} style={{ background: '#3249D7', borderColor: '#3249D7' }}>
              Apply Filter
            </Button>
          </div>
        </div>
      </Modal>

      <div ref={tableWrapRef} style={{ position: 'relative' }}>
        {/* <LeftOutlined
          onClick={() => scrollTable(-300)}
          style={{
            position: 'absolute', left: -28, top: '50%', transform: 'translateY(-50%)',
            zIndex: 10, fontSize: 46, color: 'rgba(50,73,215,0.28)',
            cursor: 'pointer', transition: 'color 0.15s',
          }}
          onMouseEnter={e => e.currentTarget.style.color = 'rgba(50,73,215,0.72)'}
          onMouseLeave={e => e.currentTarget.style.color = 'rgba(50,73,215,0.28)'}
        />
        <RightOutlined
          onClick={() => scrollTable(300)}
          style={{
            position: 'absolute', right: -28, top: '50%', transform: 'translateY(-50%)',
            zIndex: 10, fontSize: 46, color: 'rgba(50,73,215,0.28)',
            cursor: 'pointer', transition: 'color 0.15s',
          }}
          onMouseEnter={e => e.currentTarget.style.color = 'rgba(50,73,215,0.72)'}
          onMouseLeave={e => e.currentTarget.style.color = 'rgba(50,73,215,0.28)'}
        /> */}
      <Table
        rowKey="id"
        loading={studentsLoading}
        dataSource={data}
        columns={columns(handleViewProfile, handleViewAssessments, data.map(r => r.primary_email).filter(Boolean))}
        size="small"
        scroll={{ x: 1360 }}
        pagination={false}
        onChange={handleTableChange}
      />
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
        <Pagination
          size="small"
          current={students?.page || 1}
          pageSize={students?.limit || 10}
          total={students?.total || 0}
          showTotal={(t, range) => `${range[0]}–${range[1]} of ${t.toLocaleString()} students`}
          showSizeChanger
          pageSizeOptions={['10', '25', '50', '100']}
          onChange={handlePaginationChange}
        />
      </div>
      </div>

      {/* Tab-level summary charts */}
      {(data.length > 0 || ss.total_students > 0) && (
        <ChartsGrid>
          <ChartCard>
            <ChartTitle>Students by Department (top 10)</ChartTitle>
            <ResponsiveContainer width="100%" height={240}>
              <ComposedChart data={deptChartData} margin={{ top: 4, right: 24, left: -10, bottom: 48 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="dept" tick={{ fontSize: 11 }} angle={-35} textAnchor="end" interval={0} />
                <YAxis yAxisId="left" tick={{ fontSize: 11 }} />
                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} domain={[0, 100]} unit="%" />
                <Tooltip />
                <Legend verticalAlign="top" height={24} wrapperStyle={{ fontSize: 12 }} />
                <Bar yAxisId="left" dataKey="Students" fill="#3249D7" radius={[4, 4, 0, 0]} />
                <Line yAxisId="right" type="monotone" dataKey="Avg Marks" stroke="#52c41a" strokeWidth={2} dot={{ r: 3 }} />
              </ComposedChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard>
            <ChartTitle>Gender Distribution</ChartTitle>
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie
                  data={genderChartData}
                  cx="40%" cy="50%"
                  innerRadius={60} outerRadius={90}
                  paddingAngle={4}
                  dataKey="value"
                >
                  {genderChartData.map((_, i) => (
                    <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value, name) => [value.toLocaleString(), name]} />
                <Legend
                  layout="vertical"
                  align="right"
                  verticalAlign="middle"
                  formatter={(value) => {
                    const item = genderChartData.find(g => g.name === value)
                    const total = genderChartData.reduce((s, g) => s + g.value, 0)
                    const pct = item && total ? Math.round((item.value / total) * 100) : 0
                    return `${value}: ${pct}%`
                  }}
                  wrapperStyle={{ fontSize: 13, paddingLeft: 12 }}
                />
              </PieChart>
            </ResponsiveContainer>
          </ChartCard>
        </ChartsGrid>
      )}

      {/* Rich Profile Modal */}
      <Modal
        title={null}
        open={profileOpen}
        onCancel={() => setProfileOpen(false)}
        footer={null}
        width={900}
        styles={{ body: { padding: '16px', maxHeight: '85vh', overflowY: 'auto' } }}
      >
        {studentProfileLoading ? (
          <div style={{ textAlign: 'center', padding: 60 }}><Spin size="large" /></div>
        ) : studentProfile ? (
          <ProfileModalContent profile={studentProfile} />
        ) : null}
      </Modal>

      <Drawer
        title={
          assessmentStudent
            ? `${assessmentStudent.full_name || `${assessmentStudent.first_name || ''} ${assessmentStudent.last_name || ''}`.trim()} — Assessments`
            : 'Assessment Scores'
        }
        open={assessmentOpen}
        onClose={() => setAssessmentOpen(false)}
        width={460}
        styles={{ body: { background: 'linear-gradient(135deg, #e8edff 0%, #f5f0ff 40%, #e8f8f2 100%)', padding: '20px 24px' }, header: { padding: '16px 24px' } }}
      >
        {studentAssessmentsLoading ? (
          <div style={{ textAlign: 'center', padding: 48 }}><Spin /></div>
        ) : !(studentAssessments || []).length ? (
          <div style={{ textAlign: 'center', color: '#888', padding: 48 }}>No submitted assessments found</div>
        ) : (
          (studentAssessments || []).map((a, i) => {
            const cefrKey = (a.resulting_cefr || '').toUpperCase()
            const cefrMeta = CEFR_META[cefrKey] || {}
            const isComm = isCommunication(a.assessment_type)
            const isApt  = isAptitude(a.assessment_type)
            const scores = [
              ['Reading',   a.reading_score],
              ['Listening', a.listening_score],
              ['Speaking',  a.speaking_score],
            ].filter(([, v]) => v != null)
            return (
              <AssessmentCard key={i}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                  <div style={{ fontWeight: 600, fontSize: 14, color: '#1a1a2e', flex: 1, marginRight: 8 }}>
                    {a.assessment_name}
                  </div>
                  <Tag color={typeColor(a.assessment_type)} style={{ flexShrink: 0 }}>
                    {(a.assessment_type || 'Unknown').replace(/assessment/i, '').trim() || a.assessment_type}
                  </Tag>
                </div>

                <div style={{ fontSize: 12, color: '#888', marginBottom: 10 }}>
                  Submitted: {fmtDate(a.submitted_at)}
                </div>

                {isComm && cefrKey && (
                  <div style={{ marginBottom: 10 }}>
                    <span style={{ fontSize: 12, color: '#555' }}>CEFR Level:</span>
                    <CEFRBadge bg={cefrMeta.bg} color={cefrMeta.color}>{cefrKey}</CEFRBadge>
                  </div>
                )}

                {isApt && a.aptitude_level && (
                  <div style={{ marginBottom: 10, fontSize: 13 }}>
                    <span style={{ color: '#555' }}>Aptitude Level: </span>
                    <strong style={{ color: '#e65100' }}>{a.aptitude_level}</strong>
                  </div>
                )}

                {scores.length > 0 && (
                  <div style={{ marginTop: 8 }}>
                    {scores.map(([label, score]) => (
                      <div key={label} style={{ marginBottom: 6 }}>
                        <ScoreRow>
                          <span>{label}</span>
                          <span>{parseFloat(score).toFixed(1)}%</span>
                        </ScoreRow>
                        <Progress
                          percent={parseFloat(score)}
                          showInfo={false}
                          strokeColor={label === 'Reading' ? '#3249D7' : label === 'Listening' ? '#059669' : '#e65100'}
                          trailColor="rgba(0,0,0,0.06)"
                          size="small"
                        />
                      </div>
                    ))}
                  </div>
                )}
              </AssessmentCard>
            )
          })
        )}
      </Drawer>
    </div>
  )
}

export default StudentTab
